import React from "react";
import {BiddingView, DiagramPoint, NameViewMode} from "../bid/bidding_model";
import {Badge} from "../ui/badge";
import {Link} from "react-router-dom";
import {moneyFormatter} from "../../lib/formatters";


type DiagramProps = {
    bidViewData: BiddingView;
    showTitle?: boolean;
}

function DiagramLine({points, isLow}: { points: DiagramPoint[], isLow: boolean }) {
    const pad = 10; // percent padding on each side
    const toLeft = (pos: number) => pad + pos * (100 - 2 * pad);
    const lineStart = toLeft(points[0].position);
    const lineEnd = toLeft(points[points.length - 1].position);
    return (
        <div className="relative w-full" style={{height: 60}}>
            {/* horizontal line - spans from first to last circle */}
            <div
                className="absolute bg-gray-400"
                style={{left: `${lineStart}%`, right: `${100 - lineEnd}%`, height: 2, top: 30}}
            />

            {/* bidder circles */}
            {points.map((pt, i) => {
                const left = toLeft(pt.position);
                return (
                    <div key={i}>
                        {/* only show "YOU" label above the circle */}
                        {pt.yours && (
                            <div
                                className="absolute"
                                style={{left: `${left}%`, top: -6, transform: 'translateX(-50%)'}}
                            >
                                <span className={`text-xs font-bold ${isLow ? 'text-green-600' : 'text-red-600'}`}>You</span>
                            </div>
                        )}

                        {/* circle centered on the line */}
                        <div
                            className="absolute flex items-center justify-center"
                            style={{left: `${left}%`, top: 30, transform: 'translate(-50%, -50%)'}}
                        >
                            {pt.yours ? (
                                <div
                                    className={`w-5 h-5 rounded-full border ${isLow ? 'border-green-500' : 'border-red-500'} flex items-center justify-center`}
                                >
                                    <div className={`w-2 h-2 rounded-full border-2 ${isLow ? 'border-green-500 bg-green-100' : 'border-red-500 bg-red-100'}`}/>
                                </div>
                            ) : (
                                <div className="w-2 h-2 rounded-full border border-gray-500 bg-white"/>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* low / high labels under first and last circles */}
            <span
                className="absolute text-xs text-gray-400"
                style={{left: `${lineStart}%`, top: 42, transform: 'translateX(-50%)'}}
            >
                Low
            </span>
            <span
                className="absolute text-xs text-gray-400"
                style={{left: `${lineEnd}%`, top: 42, transform: 'translateX(-50%)'}}
            >
                High
            </span>
        </div>
    );
}

export default function WhereYouStandDiagram({bidViewData, showTitle = true}: DiagramProps) {
    const points = bidViewData.diagram;
    if (!points || points.length === 0) return null;

    const hasNames = bidViewData.mode.names === NameViewMode.Visible || bidViewData.mode.names === NameViewMode.Detached;
    const isLow = bidViewData.ranking === 1;
    const youColor = isLow ? "font-semibold text-green-600" : "font-semibold text-red-600";

    return (
        <div>
            {/*<div className="text-sm font-semibold mb-1">Where You Stand</div>*/}

            {showTitle && bidViewData.latest && (
                <div>
                    Your Latest Bid:{" "}
                    <Link
                        to={`/project/${bidViewData.latest.project_id}/bid/${bidViewData.latest.id}`}
                    >
                        <span className="text-lg font-semibold">
                            {moneyFormatter.format(bidViewData.latest.bid_amount)}
                        </span>
                    </Link>
                </div>
            )}

            <div className={hasNames ? "grid grid-cols-2 gap-4" : ""}>
                <div>
                    <DiagramLine points={points} isLow={isLow}/>
                    {bidViewData.ranking != null && (
                        <div className="text-sm">
                            This bid is Ranked{" "}
                            <span className="font-semibold">{bidViewData.ranking}</span>
                            {" "}out of{" "}
                            <span className="font-semibold">{bidViewData.num_bidders}</span>
                        </div>
                    )}
                </div>

                {/* ordered list of bidders */}
                {bidViewData.mode.names === NameViewMode.Visible && (
                    <ol className="text-sm text-gray-600 list-decimal list-inside">
                        {points.map((pt, i) => (
                            <li key={i} className={pt.yours ? youColor : ""}>
                                {pt.yours ? "You" : pt.name}
                            </li>
                        ))}
                    </ol>
                )}

                {/* detached names */}
                {bidViewData.mode.names === NameViewMode.Detached && (
                    <div className="text-sm text-gray-600">
                        <div className="font-medium">The other bidders are:</div>
                        <div className="space-x-2 space-y-1">
                            {bidViewData.names!.map((name) => (
                                <Badge key={name}>{name}</Badge>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
